/* GT Recently Viewed — records the current product on PDPs and renders the
   shopper's viewing history.

   LOCALE MODEL (changed 2026-07):
   The history stores ONLY stable, language-independent identifiers:
     localStorage 'gt:recently-viewed' = [{ handle, id }]   (index 0 = most recent)
   Everything the shopper actually sees — the product title, the image and the
   link — is resolved for the ACTIVE storefront locale at render time. Previously
   the title/url/image were snapshotted at view time, so a product viewed in
   Georgian kept its Georgian title after the shopper switched to English or
   Russian, and the rail showed mixed languages.

   Localized display data is kept in a separate per-locale cache:
     localStorage 'gt:recently-viewed:l10n:<iso_code>' = { handle: {title, image} }
   Because a Shopify language switch is a full page load, the new locale simply
   reads a different cache bucket — the rail is correct on the very next render.

   Cache population, cheapest path first:
     1. On a product page the section seeds the cache from the server-rendered
        (already localized) title/image of the product being viewed — no request.
     2. Anything still missing is fetched once from <root>/products/<handle>.js.
        The root is locale-prefixed (e.g. /en), so Shopify returns the localized
        product JSON. Results are cached, so steady state is zero requests.
   Links are always rebuilt as <root>/products/<handle> and therefore always
   point at the active locale.

   Guarded against double inclusion per repo JS rules. */
(function () {
  'use strict';
  if (window.gtRecentlyViewedInit) return;
  window.gtRecentlyViewedInit = true;

  var KEY = 'gt:recently-viewed';
  var CACHE_PREFIX = 'gt:recently-viewed:l10n:';
  var HISTORY_LIMIT = 24;
  var IMAGE_WIDTH = 360;

  function readJSON(key, fallback) {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(key));
      return parsed == null ? fallback : parsed;
    } catch (e) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage unavailable (private mode, quota) — feature degrades silently */
    }
  }

  /* Reads the history and migrates the legacy snapshot format
     ({handle,title,url,image}) down to stable identifiers only. Legacy titles are
     intentionally discarded: they are the source of the mixed-language bug. */
  function readHistory() {
    var raw = readJSON(KEY, []);
    if (!Array.isArray(raw)) return [];
    var seen = {};
    var out = [];
    raw.forEach(function (item) {
      if (!item || typeof item.handle !== 'string' || !item.handle) return;
      if (seen[item.handle]) return;
      seen[item.handle] = true;
      out.push({ handle: item.handle, id: item.id || null });
    });
    return out.slice(0, HISTORY_LIMIT);
  }

  function readCache(locale) {
    var cache = readJSON(CACHE_PREFIX + locale, {});
    return cache && typeof cache === 'object' && !Array.isArray(cache) ? cache : {};
  }

  /* CDN image URLs are language-independent, but they are stored per locale
     alongside the title so a single bucket read gives everything a row needs. */
  function sizedImage(src) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + IMAGE_WIDTH;
  }

  function productUrl(rootUrl, handle) {
    return rootUrl + '/products/' + encodeURIComponent(handle);
  }

  function fetchLocalized(rootUrl, handle) {
    return window
      .fetch(productUrl(rootUrl, handle) + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.title) return null;
        return { title: data.title, image: data.featured_image ? sizedImage(data.featured_image) : '' };
      })
      .catch(function () {
        return null;
      });
  }

  function buildRow(entry) {
    var li = document.createElement('li');
    li.className = 'gt-rv__item';

    var link = document.createElement('a');
    link.className = 'gt-rv__link';
    link.href = entry.url;

    var media = document.createElement('span');
    media.className = 'gt-rv__media';
    if (entry.image) {
      var img = document.createElement('img');
      img.className = 'gt-rv__img';
      img.loading = 'lazy';
      img.width = IMAGE_WIDTH;
      img.height = IMAGE_WIDTH;
      img.alt = '';
      img.src = entry.image;
      media.appendChild(img);
    }
    link.appendChild(media);

    var title = document.createElement('span');
    title.className = 'gt-rv__title';
    title.textContent = entry.title;
    link.appendChild(title);

    li.appendChild(link);
    return li;
  }

  function render(root, entries) {
    if (!entries.length) return;
    var grid = root.querySelector('[data-gt-rv-grid]');
    if (!grid || grid.childElementCount > 0) return;
    entries.forEach(function (entry) {
      grid.appendChild(buildRow(entry));
    });
    root.hidden = false;
  }

  function initSection(root) {
    var locale = root.getAttribute('data-gt-locale') || 'default';
    var rootUrl = (root.getAttribute('data-gt-root-url') || '/').replace(/\/+$/, '');
    var currentHandle = root.getAttribute('data-gt-current-handle');
    var history = readHistory();
    var cache = readCache(locale);
    var cacheDirty = false;
    var historyDirty = false;

    if (currentHandle) {
      /* Record the view (identifiers only) … */
      history = history.filter(function (item) {
        return item.handle !== currentHandle;
      });
      history.unshift({ handle: currentHandle, id: root.getAttribute('data-gt-current-id') || null });
      if (history.length > HISTORY_LIMIT) history = history.slice(0, HISTORY_LIMIT);
      historyDirty = true;

      /* … and seed this locale's cache from the server-rendered, already
         localized values, so a freshly viewed product never needs a request. */
      var currentTitle = root.getAttribute('data-gt-current-title') || '';
      if (currentTitle) {
        cache[currentHandle] = {
          title: currentTitle,
          image: root.getAttribute('data-gt-current-image') || ''
        };
        cacheDirty = true;
      }

    }

    /* The product being viewed is never shown in its own rail, but it stays in
       the persisted history. Keep the two lists separate. */
    var display = history.filter(function (item) {
      return item.handle !== currentHandle;
    });

    var max = parseInt(root.getAttribute('data-gt-max'), 10) || 8;
    var wanted = display.slice(0, max);
    if (!wanted.length) {
      if (historyDirty) writeJSON(KEY, history);
      if (cacheDirty) writeJSON(CACHE_PREFIX + locale, cache);
      return;
    }

    var missing = wanted
      .filter(function (item) {
        return !cache[item.handle] || !cache[item.handle].title;
      })
      .map(function (item) {
        return item.handle;
      });

    function finish() {
      if (historyDirty) writeJSON(KEY, history);
      if (cacheDirty) writeJSON(CACHE_PREFIX + locale, cache);
      var entries = [];
      wanted.forEach(function (item) {
        var hit = cache[item.handle];
        if (!hit || !hit.title) return;
        entries.push({ title: hit.title, image: hit.image || '', url: productUrl(rootUrl, item.handle) });
      });
      render(root, entries);
    }

    if (!missing.length || typeof window.fetch !== 'function') {
      finish();
      return;
    }

    Promise.all(
      missing.map(function (handle) {
        return fetchLocalized(rootUrl, handle).then(function (data) {
          if (data) {
            cache[handle] = data;
            cacheDirty = true;
          } else {
            /* Product is gone (404) or unreachable — drop it from the persisted
               history so it stops being retried on every page view. */
            history = history.filter(function (item) {
              return item.handle !== handle;
            });
            wanted = wanted.filter(function (item) {
              return item.handle !== handle;
            });
            historyDirty = true;
          }
        });
      })
    ).then(finish);
  }

  function boot() {
    document.querySelectorAll('[data-gt-recently-viewed]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
